import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CloseIcon from '@mui/icons-material/Close'
import { useSelector } from 'react-redux'
import { fetchUserBps } from 'services/BpService'
import { searchAssignableUsers, updateFormTemplateSharing } from 'services/taskService'
import { hideEmail } from 'utils/user'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { SkeletonLine } from 'components/Skeleton'

export const VISIBILITY_OPTIONS = [
  {
    value: 'private',
    label: 'Only me',
    description: 'Nobody else can find this form. Only you can use it on a task.',
    Icon: PersonOutlineOutlinedIcon
  },
  {
    value: 'organisation',
    label: 'Everyone in an organisation',
    description: 'Anyone signing in at a chosen email domain can find and use this form. Defaults to your own, but you can share with any organisation.',
    Icon: GroupsOutlinedIcon
  },
  {
    value: 'shared',
    label: 'Only the people I choose',
    description: 'Only the people you add below can find and use this form.',
    Icon: PersonOutlineOutlinedIcon
  }
]

export const BUILDING_SCOPE_OPTIONS = [
  {
    value: 'all',
    label: 'All buildings',
    description: 'This form can be used on a task for any building you have access to.'
  },
  {
    value: 'selected',
    label: 'Only the buildings I choose',
    description: 'This form only appears when the task is for one of the buildings you pick.'
  }
]

const normaliseSharedWith = template =>
  (template?.sharedWith || []).map(user => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    canEdit: !!user.canEdit
  }))

const FormTemplateSharingPanel = ({ template, onSaved }) => {
  const currentUser = useSelector(getCurrentUser)
  const currentUserDomain = useMemo(() => {
    const email = currentUser?.email || ''
    const at = email.lastIndexOf('@')
    return at >= 0 ? email.slice(at + 1).toLowerCase() : ''
  }, [currentUser?.email])
  const defaultDomains = currentUserDomain ? [currentUserDomain] : []

  const [visibility, setVisibility] = useState(template?.visibility || 'organisation')
  const [buildingScope, setBuildingScope] = useState(template?.buildingScope || 'all')
  const [isLocked, setIsLocked] = useState(!!template?.isLocked)
  const [sharedUsers, setSharedUsers] = useState(() => normaliseSharedWith(template))
  const [buildingPassportIds, setBuildingPassportIds] = useState(
    () => template?.buildingPassportIds || []
  )
  const [sharedDomains, setSharedDomains] = useState(
    () => (template?.sharedDomains?.length ? template.sharedDomains : defaultDomains)
  )
  const [domainInput, setDomainInput] = useState('')
  const [domainError, setDomainError] = useState('')

  const [bps, setBps] = useState([])
  const [bpsLoading, setBpsLoading] = useState(false)
  const [bpSearch, setBpSearch] = useState('')
  const bpsRequestedRef = useRef(false)

  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [savedAt, setSavedAt] = useState(null)

  useEffect(() => {
    setVisibility(template?.visibility || 'organisation')
    setBuildingScope(template?.buildingScope || 'all')
    setIsLocked(!!template?.isLocked)
    setSharedUsers(normaliseSharedWith(template))
    setBuildingPassportIds(template?.buildingPassportIds || [])
    setSharedDomains(template?.sharedDomains?.length ? template.sharedDomains : defaultDomains)
  }, [template?.id, template?.visibility, template?.buildingScope, template?.isLocked])

  useEffect(() => {
    if (buildingScope !== 'selected' || bpsRequestedRef.current) return
    bpsRequestedRef.current = true
    setBpsLoading(true)
    fetchUserBps({ page: 1, sort: 'name', filters: '&limit=250' })
      .then(result => {
        setBps(result?.data?.entries || result?.data?.data || [])
      })
      .catch(() => {
        bpsRequestedRef.current = false
        setBps([])
      })
      .finally(() => setBpsLoading(false))
  }, [buildingScope])

  useEffect(() => {
    if (visibility !== 'shared') return
    const term = userSearch.trim()
    if (term.length < 2) {
      setUserResults([])
      return
    }
    let cancelled = false
    setUserSearchLoading(true)
    const timer = setTimeout(() => {
      searchAssignableUsers({ search: term })
        .then(response => {
          if (cancelled) return
          setUserResults(response?.data?.data || response?.data || [])
        })
        .catch(() => {
          if (!cancelled) setUserResults([])
        })
        .finally(() => {
          if (!cancelled) setUserSearchLoading(false)
        })
    }, 300)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [userSearch, visibility])

  const addSharedUser = useCallback(user => {
    setSharedUsers(prev =>
      prev.some(existing => existing.id === user.id)
        ? prev
        : [...prev, {
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            canEdit: false
          }]
    )
    setUserSearch('')
    setUserResults([])
  }, [])

  const removeSharedUser = userId => {
    setSharedUsers(prev => prev.filter(user => user.id !== userId))
  }

  const toggleCanEdit = userId => {
    setSharedUsers(prev =>
      prev.map(user => (user.id === userId ? { ...user, canEdit: !user.canEdit } : user))
    )
  }

  const toggleBuilding = bpId => {
    setBuildingPassportIds(prev =>
      prev.includes(bpId) ? prev.filter(id => id !== bpId) : [...prev, bpId]
    )
  }

  const filteredBps = useMemo(() => {
    const term = bpSearch.trim().toLowerCase()
    if (!term) return bps
    return bps.filter(bp =>
      `${bp.name || ''} ${bp.address || ''} ${bp.postcode || ''}`.toLowerCase().includes(term)
    )
  }, [bps, bpSearch])

  const addDomain = () => {
    const raw = domainInput
      .trim()
      .toLowerCase()
      .replace(/^@+/, '')
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '')
    if (!raw) return
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(raw)) {
      setDomainError('Enter a valid domain, like ilivearound.com')
      return
    }
    if (!sharedDomains.includes(raw)) {
      setSharedDomains(prev => [...prev, raw])
    }
    setDomainInput('')
    setDomainError('')
  }

  const removeDomain = domain =>
    setSharedDomains(prev => prev.filter(existing => existing !== domain))

  const handleSave = async () => {
    if (!template?.id) return
    setSaving(true)
    setError('')
    try {
      const response = await updateFormTemplateSharing({
        id: template.id,
        visibility,
        building_scope: buildingScope,
        is_locked: isLocked,
        shared_users: visibility === 'shared'
          ? sharedUsers.map(user => ({ user_id: user.id, can_edit: !!user.canEdit }))
          : [],
        shared_domains: visibility === 'organisation' ? sharedDomains : [],
        building_passport_ids: buildingScope === 'selected' ? buildingPassportIds : []
      })
      setSavedAt(Date.now())
      onSaved?.(response?.data?.data || response?.data)
    } catch (requestError) {
      setError(
        requestError?.response?.data?.message ||
        'We could not save who can see this form. Please check your connection and try again.'
      )
    } finally {
      setSaving(false)
    }
  }

  if (!template?.canShare) {
    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>Who can see this form</PanelTitle>
        </PanelHeader>
        <HelperText>
          Only the person who created this form can change who it is shared with.
        </HelperText>
      </Panel>
    )
  }

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Who can see and use this form</PanelTitle>
        <PanelSubtitle>
          Choose who can find this form when they create a task, and which buildings it applies to.
        </PanelSubtitle>
      </PanelHeader>

      <Section>
        <SectionLabel>Who can see it</SectionLabel>
        <OptionList>
          {VISIBILITY_OPTIONS.map(option => (
            <OptionRow
              key={option.value}
              $selected={visibility === option.value}
              onClick={() => setVisibility(option.value)}
            >
              <OptionRadio
                type='radio'
                name='form-template-visibility'
                checked={visibility === option.value}
                onChange={() => setVisibility(option.value)}
              />
              <OptionText>
                <OptionLabel>{option.label}</OptionLabel>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionText>
            </OptionRow>
          ))}
        </OptionList>
      </Section>

      {visibility === 'organisation' && (
        <Section>
          <SectionLabel>Which organisations can use it</SectionLabel>
          <DomainHelp>
            Pre-filled with your domain. Add any email domain to share this form with that
            organisation. Anyone signing in with an address at that domain can find and use it.
          </DomainHelp>
          {sharedDomains.length > 0 && (
            <DomainChips>
              {sharedDomains.map(domain => (
                <DomainChip key={domain}>
                  <span>{domain}</span>
                  <DomainChipRemove
                    type='button'
                    onClick={() => removeDomain(domain)}
                    aria-label={`Remove ${domain}`}
                  >
                    <CloseIcon style={{ fontSize: 14 }} />
                  </DomainChipRemove>
                </DomainChip>
              ))}
            </DomainChips>
          )}
          <DomainAddRow>
            <DomainInput
              type='text'
              placeholder='e.g. ilivearound.com'
              value={domainInput}
              onChange={event => { setDomainInput(event.target.value); setDomainError('') }}
              onKeyDown={event => {
                if (event.key === 'Enter') { event.preventDefault(); addDomain() }
              }}
            />
            <AddDomainButton type='button' onClick={addDomain}>Add domain</AddDomainButton>
          </DomainAddRow>
          {domainError && <DomainError>{domainError}</DomainError>}
          {sharedDomains.length === 0 && (
            <DomainHelp>
              Add at least one domain. With none set, this form stays visible to your own organisation only.
            </DomainHelp>
          )}
        </Section>
      )}

      {visibility === 'shared' && (
        <Section>
          <SectionLabel>People who can use this form</SectionLabel>
          <SearchRow>
            <SearchOutlinedIcon style={{ fontSize: 18, color: '#8a8a94' }} />
            <SearchInput
              value={userSearch}
              onChange={event => setUserSearch(event.target.value)}
              placeholder='Search for a colleague by name'
              aria-label='Search for a colleague to share this form with'
            />
          </SearchRow>
          {userSearch.trim().length >= 2 && (
            <ResultList>
              {userSearchLoading ? (
                <SkeletonStack>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonLine key={index} $h={16} />
                  ))}
                </SkeletonStack>
              ) : userResults.length === 0 ? (
                <HelperText>No colleagues match that name.</HelperText>
              ) : (
                userResults
                  .filter(user => !sharedUsers.some(existing => existing.id === user.id))
                  .map(user => (
                    <ResultRow key={user.id} type='button' onClick={() => addSharedUser(user)}>
                      <SharedName>
                        {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Colleague'}
                      </SharedName>
                      {user.email && <SharedEmail>{hideEmail(user.email)}</SharedEmail>}
                    </ResultRow>
                  ))
              )}
            </ResultList>
          )}
          {sharedUsers.length === 0 ? (
            <HelperText>
              Nobody has been added yet. Search above to share this form with a colleague.
            </HelperText>
          ) : (
            <SharedList>
              {sharedUsers.map(user => (
                <SharedRow key={user.id}>
                  <SharedWho>
                    <SharedName>
                      {[user.firstName, user.lastName].filter(Boolean).join(' ') || 'Colleague'}
                    </SharedName>
                    {user.email && <SharedEmail>{hideEmail(user.email)}</SharedEmail>}
                  </SharedWho>
                  <SharedActions>
                    <EditToggle
                      type='button'
                      $on={user.canEdit}
                      onClick={() => toggleCanEdit(user.id)}
                      aria-pressed={user.canEdit}
                    >
                      {user.canEdit ? 'Can view and edit' : 'Can view and use'}
                    </EditToggle>
                    <RemoveButton
                      type='button'
                      onClick={() => removeSharedUser(user.id)}
                      aria-label={`Stop sharing with ${[user.firstName, user.lastName].filter(Boolean).join(' ') || 'this person'}`}
                    >
                      <CloseIcon style={{ fontSize: 18 }} />
                    </RemoveButton>
                  </SharedActions>
                </SharedRow>
              ))}
            </SharedList>
          )}
          <HelperText>
            Tap the button next to a person to change what they can do. &ldquo;Can view and use&rdquo;
            lets them put the form on a task. &ldquo;Can view and edit&rdquo; also lets them change
            the questions.
          </HelperText>
        </Section>
      )}

      <Section>
        <SectionLabel>
          <ApartmentOutlinedIcon style={{ fontSize: 18 }} />
          Which buildings it applies to
        </SectionLabel>
        <OptionList>
          {BUILDING_SCOPE_OPTIONS.map(option => (
            <OptionRow
              key={option.value}
              $selected={buildingScope === option.value}
              onClick={() => setBuildingScope(option.value)}
            >
              <OptionRadio
                type='radio'
                name='form-template-building-scope'
                checked={buildingScope === option.value}
                onChange={() => setBuildingScope(option.value)}
              />
              <OptionText>
                <OptionLabel>{option.label}</OptionLabel>
                <OptionDescription>{option.description}</OptionDescription>
              </OptionText>
            </OptionRow>
          ))}
        </OptionList>

        {buildingScope === 'selected' && (
          <BuildingBox>
            <SearchRow>
              <SearchOutlinedIcon style={{ fontSize: 18, color: '#8a8a94' }} />
              <SearchInput
                value={bpSearch}
                onChange={event => setBpSearch(event.target.value)}
                placeholder='Search your buildings by name or postcode'
                aria-label='Search your buildings'
              />
            </SearchRow>
            {bpsLoading ? (
              <SkeletonStack>
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonLine key={index} $h={18} />
                ))}
              </SkeletonStack>
            ) : filteredBps.length === 0 ? (
              <HelperText>
                {bpSearch
                  ? 'No buildings match that search. Try a different name or postcode.'
                  : 'No buildings found on your account yet.'}
              </HelperText>
            ) : (
              <BuildingList>
                {filteredBps.map(bp => (
                  <BuildingRow key={bp.id} onClick={() => toggleBuilding(bp.id)}>
                    <BuildingCheckbox
                      type='checkbox'
                      checked={buildingPassportIds.includes(bp.id)}
                      onChange={() => toggleBuilding(bp.id)}
                      onClick={event => event.stopPropagation()}
                    />
                    <BuildingText>
                      <BuildingName>{bp.name || 'Untitled building'}</BuildingName>
                      {(bp.address || bp.postcode) && (
                        <BuildingMeta>{[bp.address, bp.postcode].filter(Boolean).join(', ')}</BuildingMeta>
                      )}
                    </BuildingText>
                  </BuildingRow>
                ))}
              </BuildingList>
            )}
            <HelperText>
              {buildingPassportIds.length} building{buildingPassportIds.length === 1 ? '' : 's'} selected
            </HelperText>
          </BuildingBox>
        )}
      </Section>

      <Section>
        <SectionLabel>Can other people change this form?</SectionLabel>
        <LockRow $on={isLocked} onClick={() => setIsLocked(!isLocked)}>
          <LockIconWrap $on={isLocked}>
            {isLocked ? <LockOutlinedIcon /> : <LockOpenOutlinedIcon />}
          </LockIconWrap>
          <OptionText>
            <OptionLabel>
              {isLocked ? 'Locked. Only I can change it' : 'Unlocked. People I choose can change it'}
            </OptionLabel>
            <OptionDescription>
              {isLocked
                ? 'Other people can still use this form on a task, but nobody except you can change the questions.'
                : 'Anyone you have shared this form with as “Can view and edit” may change the questions.'}
            </OptionDescription>
          </OptionText>
          <LockSwitch
            type='checkbox'
            checked={isLocked}
            onChange={() => setIsLocked(!isLocked)}
            onClick={event => event.stopPropagation()}
            aria-label='Lock this form so only I can change it'
          />
        </LockRow>
      </Section>

      {error && (
        <ErrorBanner role='alert'>
          <ErrorOutlineIcon style={{ fontSize: 18 }} />
          <span>{error}</span>
        </ErrorBanner>
      )}

      <Footer>
        {savedAt && !saving && (
          <SavedNote>
            <CheckCircleIcon style={{ fontSize: 16 }} />
            Sharing settings saved
          </SavedNote>
        )}
        <SaveButton type='button' onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save sharing settings'}
        </SaveButton>
      </Footer>
    </Panel>
  )
}

export default FormTemplateSharingPanel

const Panel = styled.div`
  border: 1px solid #e6e6ea;
  border-radius: 12px;
  background: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 22px;
`

const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1c1c22;
`

const PanelSubtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.5;
  color: #5f5f6a;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
  color: #2c2c34;
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const OptionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  min-height: 44px;
  border-radius: 10px;
  border: 1px solid ${({ $selected }) => ($selected ? '#33becc' : '#e2e2e8')};
  background: ${({ $selected }) => ($selected ? '#e6f7f9' : '#fff')};
  cursor: pointer;

  &:hover {
    border-color: ${({ $selected }) => ($selected ? '#33becc' : '#c9c9d2')};
  }
`

const OptionRadio = styled.input`
  margin-top: 3px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #33becc;
`

const OptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1;
`

const OptionLabel = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const OptionDescription = styled.span`
  font-size: 14px;
  line-height: 1.5;
  color: #62626d;
`

const HelperText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #62626d;
`

const SharedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SharedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border: 1px solid #e6e6ea;
  border-radius: 10px;
  background: #fafafb;
`

const SharedWho = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const SharedName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const SharedEmail = styled.span`
  font-size: 13px;
  color: #7a7a85;
`

const ResultList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e6e6ea;
  border-radius: 10px;
  padding: 6px;
  background: #fff;
`

const ResultRow = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f1f1f4;
  }
`

const SharedActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #e2e2e8;
  border-radius: 8px;
  background: #fff;
  color: #62626d;
  cursor: pointer;

  &:hover {
    border-color: #b00020;
    color: #b00020;
  }
`

const EditToggle = styled.button`
  flex-shrink: 0;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid ${({ $on }) => ($on ? '#33becc' : '#d5d5dd')};
  background: ${({ $on }) => ($on ? '#33becc' : '#fff')};
  color: ${({ $on }) => ($on ? '#fff' : '#3d3d47')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`

const BuildingBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid #e6e6ea;
  border-radius: 10px;
  background: #fafafb;
`

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  min-height: 42px;
  background: #fff;
  border: 1px solid #dedee5;
  border-radius: 8px;
`

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  color: #1c1c22;
`

const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 6px 0;
`

const BuildingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 260px;
  overflow-y: auto;
`

const BuildingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  min-height: 44px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f1f1f4;
  }
`

const BuildingCheckbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #33becc;
`

const BuildingText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const BuildingName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1c1c22;
`

const BuildingMeta = styled.span`
  font-size: 13px;
  color: #7a7a85;
`

const LockRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid ${({ $on }) => ($on ? '#c2410c' : '#e2e2e8')};
  background: ${({ $on }) => ($on ? '#fff7ed' : '#fff')};
  cursor: pointer;
`

const LockIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  flex-shrink: 0;
  background: ${({ $on }) => ($on ? '#ffedd5' : '#f1f1f4')};
  color: ${({ $on }) => ($on ? '#c2410c' : '#62626d')};
`

const LockSwitch = styled.input`
  width: 20px;
  height: 20px;
  margin-top: 6px;
  cursor: pointer;
  accent-color: #c2410c;
`

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fdecea;
  color: #b00020;
  font-size: 14px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
`

const SavedNote = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #15803d;
`

const SaveButton = styled.button`
  min-height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: 8px;
  background: #33becc;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const DomainHelp = styled.p`
  margin: 0 0 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #5f5f6a;
`

const DomainChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px 5px 12px;
  border-radius: 999px;
  background: #e6f7f9;
  border: 1px solid #b8e9ee;
  color: #16343a;
  font-size: 14px;
  font-weight: 600;
`

const DomainChipRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #2aa6b3;
  cursor: pointer;

  &:hover { background: #ffffff; }
`

const DomainAddRow = styled.div`
  display: flex;
  gap: 8px;
`

const DomainInput = styled.input`
  flex: 1;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid #dedee5;
  border-radius: 8px;
  font-size: 15px;
  color: #1c1c22;
  background: #fff;

  &:focus { outline: 2px solid #33becc; outline-offset: -1px; }
`

const AddDomainButton = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid #33becc;
  border-radius: 8px;
  background: #fff;
  color: #16343a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #e6f7f9; }
`

const DomainError = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: #b00020;
`
